import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { 
  FiMaximize, FiMinimize, FiZoomIn, FiZoomOut, FiDownload,
  FiChevronDown, FiChevronRight, FiSearch, FiX, FiCalendar,
  FiClock, FiDollarSign, FiLayers, FiCheckCircle, FiAlertTriangle,
  FiGitBranch, FiCrosshair, FiMaximize2, FiInfo, FiFilter,
  FiTrendingUp, FiUsers, FiBriefcase, FiSettings
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

// ==================== IMPLEMENTATION MODE ====================

const IMPLEMENTATION_MODES = {
  fast: {
    name: 'Fast Track',
    icon: '🚀',
    multiplier: 0.6,
    costMultiplier: 1.3,
    qualityMultiplier: 0.8,
    description: 'Accelerated timeline with parallel execution'
  },
  normal: {
    name: 'Normal',
    icon: '⚖️',
    multiplier: 1.0,
    costMultiplier: 1.0,
    qualityMultiplier: 1.0,
    description: 'Balanced timeline with standard quality'
  },
  quality: {
    name: 'Quality Focused',
    icon: '🎯',
    multiplier: 1.5,
    costMultiplier: 1.2,
    qualityMultiplier: 1.3,
    description: 'Extended timeline with enhanced quality gates'
  }
};

// ==================== WBS GENERATOR ====================

const generateWBS = (components, mode, scale, industry) => {
  const modeConfig = IMPLEMENTATION_MODES[mode] || IMPLEMENTATION_MODES.normal;
  const componentCount = components?.length || 0;
  
  // Calculate total project duration based on components and mode
  const baseDuration = componentCount > 40 ? 24 : componentCount > 25 ? 18 : componentCount > 15 ? 12 : 8; // weeks
  const totalDuration = Math.round(baseDuration * modeConfig.multiplier);
  
  // Calculate total cost
  const totalComponentCost = components?.reduce((sum, c) => sum + ((c.cost?.high || 0)), 0) || 0;
  const implementationCost = totalComponentCost * modeConfig.costMultiplier;
  
  const wbs = {
    id: 'wbs-root',
    name: `${industry || 'Enterprise'} AI Solution Implementation`,
    type: 'project',
    duration: `${totalDuration} weeks`,
    cost: implementationCost,
    startDate: null,
    children: [
      {
        id: 'wbs-1',
        name: 'Phase 1: Foundation & Discovery',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.15))} weeks`,
        cost: implementationCost * 0.10,
        children: [
          { id: 'wbs-1-1', name: '1.1 Stakeholder Analysis & Engagement', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-1-2', name: '1.2 Current State Assessment', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-1-3', name: '1.3 Requirements Validation & Finalization', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-1-4', name: '1.4 Architecture Review & Approval', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-1-5', name: '1.5 Data Governance Framework Setup', type: 'task', duration: '1 week', cost: implementationCost * 0.02 }
        ]
      },
      {
        id: 'wbs-2',
        name: 'Phase 2: Infrastructure & Environment Setup',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.20))} weeks`,
        cost: implementationCost * 0.20,
        children: [
          { id: 'wbs-2-1', name: '2.1 Infrastructure Provisioning', type: 'task', duration: '1 week', cost: implementationCost * 0.05 },
          { id: 'wbs-2-2', name: '2.2 Network & Security Configuration', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-2-3', name: '2.3 Data Pipeline Setup', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-2-4', name: '2.4 MLOps Platform Deployment', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-2-5', name: '2.5 CI/CD Pipeline Configuration', type: 'task', duration: '1 week', cost: implementationCost * 0.03 }
        ]
      },
      {
        id: 'wbs-3',
        name: 'Phase 3: Data Management & Preparation',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.15))} weeks`,
        cost: implementationCost * 0.15,
        children: [
          { id: 'wbs-3-1', name: '3.1 Data Collection & Integration', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-3-2', name: '3.2 Data Cleaning & Validation', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-3-3', name: '3.3 Feature Engineering Pipeline', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-3-4', name: '3.4 Data Quality Gates Implementation', type: 'task', duration: '1 week', cost: implementationCost * 0.04 }
        ]
      },
      {
        id: 'wbs-4',
        name: 'Phase 4: Model Development & Training',
        type: 'phase',
        duration: `${Math.max(3, Math.round(totalDuration * 0.25))} weeks`,
        cost: implementationCost * 0.25,
        children: [
          { id: 'wbs-4-1', name: '4.1 Model Architecture Design', type: 'task', duration: '1 week', cost: implementationCost * 0.05 },
          { id: 'wbs-4-2', name: '4.2 Model Training & Tuning', type: 'task', duration: '2 weeks', cost: implementationCost * 0.08 },
          { id: 'wbs-4-3', name: '4.3 Model Evaluation & Validation', type: 'task', duration: '1 week', cost: implementationCost * 0.05 },
          { id: 'wbs-4-4', name: '4.4 Model Interpretability & Explainability', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-4-5', name: '4.5 Model Optimization & Compression', type: 'task', duration: '1 week', cost: implementationCost * 0.03 }
        ]
      },
      {
        id: 'wbs-5',
        name: 'Phase 5: Integration & Deployment',
        type: 'phase',
        duration: `${Math.max(3, Math.round(totalDuration * 0.15))} weeks`,
        cost: implementationCost * 0.15,
        children: [
          { id: 'wbs-5-1', name: '5.1 API Integration', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-5-2', name: '5.2 System Integration Testing', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-5-3', name: '5.3 User Acceptance Testing', type: 'task', duration: '1 week', cost: implementationCost * 0.04 },
          { id: 'wbs-5-4', name: '5.4 Production Deployment', type: 'task', duration: '1 week', cost: implementationCost * 0.04 }
        ]
      },
      {
        id: 'wbs-6',
        name: 'Phase 6: Testing & Quality Assurance',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.10))} weeks`,
        cost: implementationCost * 0.10,
        children: [
          { id: 'wbs-6-1', name: '6.1 Performance Testing', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-6-2', name: '6.2 Security Testing', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-6-3', name: '6.3 Compliance Validation', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-6-4', name: '6.4 Load & Stress Testing', type: 'task', duration: '1 week', cost: implementationCost * 0.02 }
        ]
      },
      {
        id: 'wbs-7',
        name: 'Phase 7: Training & Change Management',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.10))} weeks`,
        cost: implementationCost * 0.08,
        children: [
          { id: 'wbs-7-1', name: '7.1 User Training Programs', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-7-2', name: '7.2 Documentation & Knowledge Transfer', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-7-3', name: '7.3 Change Management Communication', type: 'task', duration: '1 week', cost: implementationCost * 0.02 }
        ]
      },
      {
        id: 'wbs-8',
        name: 'Phase 8: Go-Live & Hypercare',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.10))} weeks`,
        cost: implementationCost * 0.07,
        children: [
          { id: 'wbs-8-1', name: '8.1 Go-Live Execution', type: 'task', duration: '1 week', cost: implementationCost * 0.03 },
          { id: 'wbs-8-2', name: '8.2 Hypercare Support', type: 'task', duration: '2 weeks', cost: implementationCost * 0.02 },
          { id: 'wbs-8-3', name: '8.3 Performance Monitoring', type: 'task', duration: '1 week', cost: implementationCost * 0.02 }
        ]
      },
      {
        id: 'wbs-9',
        name: 'Phase 9: Optimization & Continuous Improvement',
        type: 'phase',
        duration: `${Math.max(2, Math.round(totalDuration * 0.10))} weeks`,
        cost: implementationCost * 0.05,
        children: [
          { id: 'wbs-9-1', name: '9.1 Model Performance Optimization', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-9-2', name: '9.2 Cost Optimization', type: 'task', duration: '1 week', cost: implementationCost * 0.02 },
          { id: 'wbs-9-3', name: '9.3 Feedback Loop Implementation', type: 'task', duration: '1 week', cost: implementationCost * 0.01 }
        ]
      }
    ]
  };
  
  return wbs;
};

// ==================== MAIN WBS VIEW COMPONENT ====================

const WBSView = () => {
  const { state } = useSolution();
  const [implementationMode, setImplementationMode] = useState('normal');
  const [startDate, setStartDate] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set(['wbs-root', 'wbs-1', 'wbs-2', 'wbs-3', 'wbs-4', 'wbs-5', 'wbs-6', 'wbs-7', 'wbs-8', 'wbs-9']));
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [showAllPhases, setShowAllPhases] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const mainContainerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  
  // ==================== GENERATE WBS ====================
  
  const wbsData = useMemo(() => {
    const industry = state.autoArchitectRequirements?.industry || 'Enterprise';
    const scale = state.autoArchitectRequirements?.scale || 'smb';
    return generateWBS(state.selectedComponents, implementationMode, scale, industry);
  }, [state.selectedComponents, implementationMode, state.autoArchitectRequirements]);
  
  // ==================== CALCULATE DATES ====================
  
  const calculateDates = useCallback((wbs, startDateStr) => {
    if (!startDateStr) return wbs;
    
    const startDateObj = new Date(startDateStr);
    const addWeeks = (date, weeks) => {
      const result = new Date(date);
      result.setDate(result.getDate() + weeks * 7);
      return result;
    };
    
    const assignDates = (node, currentStart) => {
      const durationWeeks = parseInt(node.duration) || 0;
      const endDate = addWeeks(currentStart, durationWeeks);
      node.startDate = currentStart.toISOString().split('T')[0];
      node.endDate = endDate.toISOString().split('T')[0];
      
      let childStart = new Date(currentStart);
      if (node.children) {
        node.children.forEach(child => {
          assignDates(child, childStart);
          const childWeeks = parseInt(child.duration) || 0;
          childStart = addWeeks(childStart, childWeeks);
        });
      }
    };
    
    const wbsWithDates = JSON.parse(JSON.stringify(wbs));
    assignDates(wbsWithDates, startDateObj);
    return wbsWithDates;
  }, []);
  
  const wbsWithDates = useMemo(() => {
    return calculateDates(wbsData, startDate);
  }, [wbsData, startDate, calculateDates]);
  
  // ==================== FLATTEN WBS FOR RENDERING ====================
  
  const flattenWBS = useCallback((node, level = 0, parentExpanded = true) => {
    const result = [];
    const isExpanded = expandedNodes.has(node.id);
    const shouldShow = parentExpanded || level === 0;
    
    if (!shouldShow) return result;
    
    result.push({ ...node, level, isExpanded });
    
    if (node.children && isExpanded) {
      node.children.forEach(child => {
        result.push(...flattenWBS(child, level + 1, isExpanded));
      });
    }
    
    return result;
  }, [expandedNodes]);
  
  const flatList = useMemo(() => {
    return flattenWBS(wbsWithDates);
  }, [wbsWithDates, flattenWBS]);
  
  // ==================== STATS ====================
  
  const stats = useMemo(() => {
    const totalPhases = wbsWithDates.children?.length || 0;
    const totalTasks = wbsWithDates.children?.reduce((sum, phase) => sum + (phase.children?.length || 0), 0) || 0;
    const totalDuration = parseInt(wbsWithDates.duration) || 0;
    const totalCost = wbsWithDates.cost || 0;
    
    return {
      totalPhases,
      totalTasks,
      totalDuration,
      totalCost,
      totalComponents: state.selectedComponents.length
    };
  }, [wbsWithDates, state.selectedComponents]);
  
  // ==================== TOGGLE FUNCTIONS ====================
  
  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);
  
  const expandAll = useCallback(() => {
    const allIds = new Set(['wbs-root']);
    wbsData.children?.forEach(phase => {
      allIds.add(phase.id);
      phase.children?.forEach(task => allIds.add(task.id));
    });
    setExpandedNodes(allIds);
  }, [wbsData]);
  
  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set(['wbs-root']));
  }, []);
  
  // ==================== FULLSCREEN ====================
  
  const toggleFullscreen = useCallback(() => {
    const element = mainContainerRef.current;
    if (!element) return;
    
    if (!document.fullscreenElement) {
      element.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  // F11 shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);
  
  // ==================== ZOOM ====================
  
  const setupZoom = useCallback(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => setTransform(event.transform));
    svg.call(zoom);
    svg.on('dblclick.zoom', null);
  }, []);
  
  useEffect(() => {
    setTimeout(() => setupZoom(), 100);
  }, [setupZoom]);
  
  // ==================== RENDER NODE ====================
  
  const renderNode = (node, index) => {
    const isProject = node.type === 'project';
    const isPhase = node.type === 'phase';
    const isTask = node.type === 'task';
    const isHovered = hoveredNode === node.id;
    const isSelected = selectedNode === node.id;
    
    const indent = node.level * 30;
    const backgroundColor = isProject ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' :
                            isPhase ? 'bg-blue-50' :
                            isTask ? 'bg-white' : 'bg-gray-50';
    
    const nodeHeight = isProject ? 50 : isPhase ? 42 : 36;
    
    return (
      <div
        key={node.id}
        className={`flex items-center border-b border-gray-100 cursor-pointer transition-all ${backgroundColor} ${
          isHovered ? 'ring-2 ring-blue-400' : ''
        } ${isSelected ? 'ring-2 ring-red-400' : ''}`}
        style={{ paddingLeft: `${indent + 10}px`, height: nodeHeight }}
        onClick={() => {
          if (isProject || isPhase) toggleNode(node.id);
          setSelectedNode(node.id);
        }}
        onMouseEnter={(e) => {
          setHoveredNode(node.id);
          setTooltip({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            content: node
          });
        }}
        onMouseLeave={() => {
          setHoveredNode(null);
          setTooltip({ visible: false, x: 0, y: 0, content: null });
        }}
      >
        {/* Expand/Collapse indicator */}
        {node.children && node.children.length > 0 && (
          <span className="mr-2">
            {node.isExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
          </span>
        )}
        
        {/* Node name */}
        <span className={`flex-1 font-medium ${isProject ? 'font-bold text-lg' : isPhase ? 'font-semibold' : 'text-sm'}`}>
          {node.name}
        </span>
        
        {/* Duration */}
        <span className="w-24 text-center text-sm">
          <FiClock className="w-3 h-3 inline mr-1" />
          {node.duration}
        </span>
        
        {/* Cost */}
        <span className="w-32 text-right text-sm font-semibold text-green-600">
          <FiDollarSign className="w-3 h-3 inline mr-1" />
          ${(node.cost || 0).toLocaleString()}
        </span>
        
        {/* Dates */}
        {startDate && (
          <span className="w-56 text-center text-xs text-gray-500">
            <FiCalendar className="w-3 h-3 inline mr-1" />
            {node.startDate || '—'} → {node.endDate || '—'}
          </span>
        )}
      </div>
    );
  };
  
  // ==================== MAIN RENDER ====================
  
  return (
    <div 
      ref={mainContainerRef}
      className={`h-full flex flex-col ${isFullscreen ? 'bg-white' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiBriefcase className="w-6 h-6 text-blue-600" />
              Work Breakdown Structure (WBS)
            </h2>
            <p className="text-sm text-gray-500">
              End-to-end implementation plan with {stats.totalPhases} phases and {stats.totalTasks} tasks
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Implementation Mode */}
            <select
              value={implementationMode}
              onChange={(e) => setImplementationMode(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="fast">🚀 Fast Track</option>
              <option value="normal">⚖️ Normal</option>
              <option value="quality">🎯 Quality Focused</option>
            </select>
            
            {/* Start Date */}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            {/* Expand/Collapse All */}
            <button onClick={expandAll} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200">
              Expand All
            </button>
            <button onClick={collapseAll} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200">
              Collapse All
            </button>
            
            {/* Zoom */}
            <button onClick={() => setTransform(prev => ({ ...prev, k: Math.min(prev.k * 1.2, 3) }))} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Zoom In">
              <FiZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => setTransform(prev => ({ ...prev, k: Math.max(prev.k / 1.2, 0.3) }))} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Zoom Out">
              <FiZoomOut className="w-4 h-4" />
            </button>
            <button onClick={() => setTransform({ x: 0, y: 0, k: 1 })} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200" title="Reset">
              <FiCrosshair className="w-4 h-4" />
            </button>
            
            {/* Fullscreen */}
            <button onClick={toggleFullscreen} className={`p-2 rounded-lg ${isFullscreen ? 'bg-red-100 text-red-700' : 'bg-gray-100 hover:bg-gray-200'}`} title="Fullscreen (F11)">
              {isFullscreen ? <FiMinimize className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
        
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-3 bg-blue-50 rounded-lg text-center">
            <div className="text-xl font-bold text-blue-600">{stats.totalPhases}</div>
            <div className="text-xs text-gray-600">Phases</div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg text-center">
            <div className="text-xl font-bold text-green-600">{stats.totalTasks}</div>
            <div className="text-xs text-gray-600">Tasks</div>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg text-center">
            <div className="text-xl font-bold text-purple-600">{stats.totalDuration} wks</div>
            <div className="text-xs text-gray-600">Duration</div>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg text-center">
            <div className="text-xl font-bold text-yellow-600">${(stats.totalCost / 1000).toFixed(0)}K</div>
            <div className="text-xs text-gray-600">Est. Cost</div>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg text-center">
            <div className="text-xl font-bold text-orange-600">{stats.totalComponents}</div>
            <div className="text-xs text-gray-600">Components</div>
          </div>
        </div>
      </div>
      
      {/* WBS Content */}
      <div className="flex-1 overflow-auto" ref={containerRef}>
        <div 
          style={{ transform: `scale(${transform.k})`, transformOrigin: 'top left' }}
        >
          {/* Column Headers */}
          <div className="flex items-center bg-gray-800 text-white px-3 py-2 sticky top-0 z-10">
            <span className="flex-1 font-semibold text-sm pl-8">Task</span>
            <span className="w-24 text-center font-semibold text-sm">Duration</span>
            <span className="w-32 text-right font-semibold text-sm">Cost</span>
            {startDate && <span className="w-56 text-center font-semibold text-sm">Timeline</span>}
          </div>
          
          {/* Nodes */}
          <div className="bg-white">
            {flatList.map((node, index) => renderNode(node, index))}
          </div>
        </div>
      </div>
      
      {/* Tooltip */}
      <AnimatePresence>
        {tooltip.visible && tooltip.content && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-4 max-w-xs pointer-events-none"
            style={{ left: tooltip.x + 15, top: tooltip.y + 15 }}
          >
            <div className="font-bold text-gray-900 mb-1">{tooltip.content.name}</div>
            <div className="text-xs text-gray-500 mb-2">
              Type: {tooltip.content.type?.charAt(0).toUpperCase() + tooltip.content.type?.slice(1)}
            </div>
            <div className="text-xs text-gray-600">
              Duration: <strong>{tooltip.content.duration}</strong>
            </div>
            <div className="text-xs text-gray-600">
              Cost: <strong>${(tooltip.content.cost || 0).toLocaleString()}</strong>
            </div>
            {tooltip.content.startDate && (
              <div className="text-xs text-gray-600">
                Timeline: {tooltip.content.startDate} → {tooltip.content.endDate}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Legend */}
      <div className="bg-white border-t border-gray-200 p-2 text-xs text-gray-600 flex items-center gap-4 flex-wrap">
        <span className="font-semibold">Legend:</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded"></span> Project</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-50 rounded"></span> Phase</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-gray-200 rounded"></span> Task</span>
        <span className="text-gray-400">|</span>
        <span>🖱️ Click to expand/collapse • Scroll to zoom</span>
        <span className="text-gray-400">|</span>
        <span>Mode: {IMPLEMENTATION_MODES[implementationMode].name} ({IMPLEMENTATION_MODES[implementationMode].description})</span>
      </div>
    </div>
  );
};

export default WBSView;